import { useState, useEffect, useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { useDataContext } from "../contexts/AuthContext";
import { useDebtsContext } from "../contexts/FeeContext";
import {
  getPaymentInformation,
  getFeeInfo,
  fetchFeesWithInterest,
  getGetnetLink,
} from "../services/api";
import Modal2 from "./Modal2";
import Modal3 from "./Modal3";
import ModalInfoPago from "./ModalIfoPago";
import logoPng from "../images/Frame.png";
import s from "../styles/Dashboard.module.css";
import { BANK_TRANSFER } from "../config/littleBlue";

// Copia robusta (navigator.clipboard solo anda en contexto seguro; fallback execCommand).
const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => {});
    return;
  }
  const t = document.createElement("textarea");
  t.value = text;
  t.style.position = "fixed";
  t.style.opacity = "0";
  document.body.appendChild(t);
  t.focus();
  t.select();
  try { document.execCommand("copy"); } catch (e) { /* noop */ }
  document.body.removeChild(t);
};

function Payments() {
  const { contextData } = useDataContext();
  const { userDebts } = useDebtsContext();
  const { orderType: ctxOrderType } = useOutletContext() ?? {};

  const customer = contextData?.customer ?? {};
  const orderType = ctxOrderType ?? customer.order_type ?? 1;

  const [paymentInformation, setPaymentInformation] = useState({});
  const [paid, setPaid] = useState(0);
  const [total, setTotal] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [getnetLink, setGetnetLink] = useState(null); // link GetNet que matchea la selección (tarjeta)
  const [feesWithInterest, setFeesWithInterest] = useState([]);
  const [hasPaymentsRecords, setHasPendingRecords] = useState(false);
  const [hasPaymentsPending, setHasPendingPayments] = useState(false);
  const [hasAnySizeApproved, setHasAnySizeApproved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showModalInfoPago, setShowModalInfoPago] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [isModal3Open, setIsModal3Open] = useState(false);

  const [isBotonHabilitado, setIsBotonHabilitado] = useState(true);
  const [activeFeeDetailId, setActiveFeeDetailId] = useState(null);
  const [feeDetailById, setFeeDetailById] = useState({});
  const feeDetailRefs = useRef({});

  useEffect(() => {
    // El botón "Informar pago" solo se deshabilita si hay un pago PENDIENTE de aprobación
    // (para no informar dos veces). No por tener pagos históricos: con hasPaymentsRecords,
    // todo cliente con 2+ pagos (toda la base migrada de V3) quedaba sin poder informar.
    setIsBotonHabilitado(!hasPaymentsPending);
  }, [hasPaymentsPending]);

  // Si paga con tarjeta de crédito, buscamos el link GetNet que coincide con su selección.
  useEffect(() => {
    if (!customer.id || !customer.order_id) return;
    if (!String(paymentMethod || "").toLowerCase().includes("tarjeta")) {
      setGetnetLink(null);
      return;
    }
    getGetnetLink(customer.id, customer.order_id)
      .then((data) => setGetnetLink(data?.link || null))
      .catch((e) => console.error("No se pudo obtener el link GetNet:", e));
  }, [customer.id, customer.order_id, paymentMethod]);

  useEffect(() => {
    if (!customer.id) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getPaymentInformation(customer.id, customer.order_id);
        if (cancelled) return;
        setPaymentInformation(data);
        setPaymentMethod(data.payment_method);
        setPaid(data.paid);
        setTotal(data.total);
        setHasPendingRecords(data.hasPaymentsRecords);
        setHasPendingPayments(data.hasPaymentsPending);
        setHasAnySizeApproved(Boolean(data.has_any_size_approved));
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [customer.id, customer.order_id]);

  useEffect(() => {
    if (!userDebts) return;
    fetchFeesWithInterest(userDebts)
      .then(setFeesWithInterest)
      .catch(console.error);
  }, [userDebts]);

  const progress = useMemo(() => {
    if (!total || parseFloat(total) === 0) return 0;
    return Math.min(100, (parseFloat(paid) / parseFloat(total)) * 100);
  }, [paid, total]);

  const progressGrad = "linear-gradient(90deg,#D449B8,#a020c8)";

  const cuotasConEstilo = useMemo(() => {
    const currentDate = new Date();
    return feesWithInterest.map((c) => {
      const extraType = Number(c.is_extra || 0);
      const exp = new Date(c.expiration_date);
      const formattedDate = exp.toLocaleDateString("es-ES", { timeZone: "UTC" });
      let colorClass = s.cuotaFutura;
      let hasExpired = false;
      if (currentDate > exp) {
        colorClass = s.cuotaVencida;
        hasExpired = true;
      } else if (
        exp.getMonth() === currentDate.getMonth() &&
        exp.getFullYear() === currentDate.getFullYear()
      ) {
        colorClass = s.cuotaProxima;
      }
      // Monto real a pagar por la cuota = capital pendiente + mora.
      const montoConMora = parseFloat(c.pending || 0) + parseFloat(c.interest || 0);
      return { ...c, is_extra: extraType, colorClass, hasExpired, formattedDate, montoConMora };
    });
  }, [feesWithInterest]);

  const proximaCuota = useMemo(() => {
    const pendientes = cuotasConEstilo
      .filter((c) => Number(c.is_extra || 0) === 0 && parseFloat(c.pending) > 0)
      .sort((a, b) => a.number - b.number);
    if (pendientes[0]) return pendientes[0];
    // Si ya no quedan cuotas NORMALES pendientes, ofrecer informar la próxima EXTRA pendiente
    // (prenda/cuota extra). Antes quedaba sin botón "Informar pago" y no se podía pagar la extra.
    const extras = cuotasConEstilo
      .filter((c) => Number(c.is_extra || 0) > 0 && parseFloat(c.pending) > 0)
      .sort((a, b) => (a.number || 0) - (b.number || 0));
    if (extras[0]) return extras[0];
    // Sin NINGUNA cuota con saldo pendiente, pero la cuenta todavía debe (p.ej. interés/mora
    // en vivo o un costo residual que V4 cuenta en la deuda pero no vive en el `pending` de una
    // cuota): igual hay que poder informar el pago. Ofrecemos una "cuota" sintética por el saldo
    // (total autoritativo de V4 − pagado). Sin esto el cliente quedaba sin botón "Informar pago"
    // (caso Danilo Arias 2026-000684: capital saldado, $2.211 de mora sin forma de informar).
    const saldoResidual = Math.round((parseFloat(total) || 0) - (parseFloat(paid) || 0));
    if (saldoResidual > 200) { // 200 = tolerancia de saldo de V4
      return {
        number: null,
        is_extra: 0,
        pending: saldoResidual,
        montoConMora: saldoResidual,
        formattedDate: null,
        __saldoResidual: true,
      };
    }
    return null;
  }, [cuotasConEstilo, total, paid]);

  const handleFormularioEnviado = () => setIsBotonHabilitado(false);

  const canHoverFeeDetail = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const openFeeDetail = async (id) => {
    setActiveFeeDetailId(id);
    if (feeDetailById[id]) return;
    try {
      const detail = await getFeeInfo(id);
      setFeeDetailById((prev) => ({ ...prev, [id]: detail }));
    } catch {
      /* silencioso */
    }
  };

  const closeFeeDetail = () => {
    setActiveFeeDetailId(null);
  };

  const toggleFeeDetail = (id) => {
    setActiveFeeDetailId((prev) => (prev === id ? null : id));
    if (!feeDetailById[id]) {
      openFeeDetail(id);
    }
  };

  useEffect(() => {
    if (!activeFeeDetailId) return undefined;

    const handlePointerDown = (event) => {
      const activeNode = feeDetailRefs.current[activeFeeDetailId];
      if (!activeNode || activeNode.contains(event.target)) return;
      setActiveFeeDetailId(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveFeeDetailId(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeFeeDetailId]);

  if (loading) {
    return (
      <div className="contcargando">
        <img src={logoPng} alt="Little Blue" className="imgcargando" />
        <h2 className="text-muted">CARGANDO...</h2>
        <div className="loader" />
      </div>
    );
  }

  return (
    <>
      {isModal3Open && (
        <Modal3
          onFormularioEnviado={handleFormularioEnviado}
          toggleModal3={() => setIsModal3Open(false)}
        />
      )}
      {showModalInfoPago && (
        <ModalInfoPago toggleModalInfo={() => setShowModalInfoPago(false)} />
      )}
      {showModal2 && orderType !== 2 && (
        <Modal2
          onAcceptClick={() => {
            setShowModal2(false);
            localStorage.setItem("paymentMethodSelected", "true");
          }}
          onBackClick={() => setShowModal2(false)}
          setPaymentInformation={setPaymentInformation}
          setPaymentMethod={setPaymentMethod}
          setTotal={setTotal}
          setPaid={setPaid}
          vengodelModal1={false}
          vengodelModal2={() => {}}
          handleAtrasClick={() => setShowModal2(false)}
          isBlockedByApprovedSizes={hasAnySizeApproved}
        />
      )}

      <div className={s.page}>
        <div className={s.pageHeader}>
          <h1 className={`${s.pageHeaderTitle} ${s.pageHeaderTitleCompact}`}>Mis pagos</h1>
        </div>

        <div className={`${s.progressCard} ${s.progressCardPrimary}`} style={{ margin: "1em 1em 0" }}>
          <div className={s.progressHeader}>
            <span className={s.progressLabel}>Tu avance de pago</span>
            <span className={s.progressPct}>{Math.round(progress)}%</span>
          </div>
          <div className={s.progressTrack}>
            <div
              className={s.progressFill}
              style={{ width: `${Math.max(0, Math.min(100, progress))}%`, background: progressGrad }}
            />
          </div>
          <div className={s.progressAmounts}>
            <div className={s.progressAmount}>
              <span className={s.progressAmountLabel}>Abonado</span>
              <strong className={s.progressAmountValue} style={{ color: "#00A651" }}>
                ${Number(paid || 0).toLocaleString("es-AR")}
              </strong>
            </div>
            <div className={`${s.progressAmount} ${s.progressAmountRight}`}>
              <span className={s.progressAmountLabel}>Total</span>
              <strong className={s.progressAmountValue}>
                ${Number(total || 0).toLocaleString("es-AR")}
              </strong>
            </div>
          </div>
          {hasPaymentsPending ? (
            <p className={s.pendingBadge}>Tenés pagos pendientes de aprobación</p>
          ) : null}
        </div>

        {proximaCuota && orderType !== 2 && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>{proximaCuota.__saldoResidual ? "Saldo pendiente" : "Próximo vencimiento"}</h2>
            <div className={s.nextPayCard}>
              <span className={s.nextPayIcon}>
                <i className="bi bi-calendar2-week-fill" aria-hidden="true" />
              </span>
              <div className={s.nextPayBody}>
                <span className={s.nextPayLabel}>{proximaCuota.__saldoResidual ? "Saldo pendiente" : (Number(proximaCuota.is_extra || 0) > 0 ? (Number(proximaCuota.is_extra) === 2 ? "Prenda extra" : "Cuota extra") : `${proximaCuota.number}° cuota`)}</span>
                <span className={s.nextPayAmount}>
                  ${parseFloat(proximaCuota.montoConMora).toLocaleString("es-AR")}
                </span>
                {proximaCuota.formattedDate ? (
                  <span className={s.nextPayDate}>Vence {proximaCuota.formattedDate}</span>
                ) : null}
              </div>
              <button
                className={s.nextPayBtn}
                onClick={() => setIsModal3Open(true)}
                disabled={!isBotonHabilitado}
              >
                Informar pago
              </button>
            </div>
          </section>
        )}

        {orderType !== 2 && paymentMethod && (
          <section className={s.section}>
            <div className={s.payMethodRow}>
              <span className={s.payMethodLabel}>
                Modalidad de pago:{" "}
                <strong className={s.payMethodValue}>{paymentMethod.toUpperCase()}</strong>
              </span>
              <div className={s.payMethodActions}>
                <button
                  className={s.btnSecondary}
                  onClick={() => setShowModalInfoPago(true)}
                >
                  Más info
                </button>
                <button
                  className={`${s.btnSecondary} ${(!isBotonHabilitado || hasAnySizeApproved) ? s.btnDisabled : ""}`}
                  disabled={!isBotonHabilitado || hasAnySizeApproved}
                  onClick={() => setShowModal2(true)}
                >
                  Cambiar
                </button>
              </div>
            </div>

            {/* Tarjeta de crédito: link de pago GetNet que coincide con la selección. */}
            {String(paymentMethod || "").toLowerCase().includes("tarjeta") && (
              getnetLink && getnetLink.url ? (
                <a
                  href={getnetLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: ".5em",
                    marginTop: ".7em", textDecoration: "none", borderRadius: ".8em",
                    background: "#0071ce", color: "#fff", padding: ".8em", fontWeight: 800, fontSize: ".95em",
                  }}
                >
                  <i className="bi bi-credit-card-2-front-fill" /> Pagar con tarjeta (link GetNet)
                </a>
              ) : (
                <p style={{ marginTop: ".7em", padding: ".7em .8em", borderRadius: ".7em", background: "#fff7ed", color: "#9a3412", fontSize: ".85em", border: "1px solid #fed7aa" }}>
                  <i className="bi bi-info-circle" /> Solicitá el link de pago a tu asesor.
                </p>
              )
            )}

            {/* Transferencia / Depósito: datos bancarios de Little Blue para transferir. */}
            {["transferencia", "deposito", "depósito"].some((m) => String(paymentMethod || "").toLowerCase().includes(m)) && (
              <div style={{ marginTop: ".7em", padding: ".9em 1em", borderRadius: ".8em", background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                <p style={{ fontWeight: 800, color: "#1e3a8a", marginBottom: ".5em" }}>
                  <i className="bi bi-bank" /> Datos para transferir
                </p>
                {[["CBU", BANK_TRANSFER.cbu], ["Alias", BANK_TRANSFER.alias], ["CUIT", BANK_TRANSFER.cuit], ["Cuenta", BANK_TRANSFER.cuenta]].map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5em", padding: ".28em 0", borderTop: label === "CBU" ? "none" : "1px solid #dbeafe" }}>
                    <span style={{ color: "#475569", fontSize: ".82em" }}>{label}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: ".5em", minWidth: 0 }}>
                      <strong style={{ color: "#0f172a", fontSize: ".9em", wordBreak: "break-all", textAlign: "right" }}>{value}</strong>
                      <button type="button" onClick={() => copyText(value)} title="Copiar" style={{ flexShrink: 0, border: "none", background: "transparent", cursor: "pointer", color: "#0071ce", fontSize: "1em" }}>
                        <i className="bi bi-clipboard" />
                      </button>
                    </span>
                  </div>
                ))}
                <p style={{ marginTop: ".6em", fontSize: ".78em", color: "#64748b" }}>
                  Después informá el comprobante desde el dashboard.
                </p>
              </div>
            )}
          </section>
        )}

        {cuotasConEstilo.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Plan de pagos</h2>

            <div className={s.feeList}>
              {cuotasConEstilo
                .filter((c) => Number(c.is_extra || 0) === 0)
                .sort((a, b) => a.number - b.number)
                .map((c) => (
                  <div key={c.id} className={`${s.feeRow} ${c.colorClass}`}>
                    <span className={s.feeNum}>{c.number}°</span>
                    <div className={s.feeBody}>
                      <span className={s.feeName}>{c.number}° Cuota</span>
                      <span className={s.feeDate}>{c.formattedDate}</span>
                    </div>
                    <span className={s.feeAmount}>
                      ${parseFloat(c.montoConMora).toLocaleString("es-AR")}
                    </span>
                    <span className={s.feeDot} />
                    {c.hasExpired && (
                      <div
                        className={s.feeQuestionWrap}
                        ref={(node) => {
                          if (node) {
                            feeDetailRefs.current[c.id] = node;
                          } else {
                            delete feeDetailRefs.current[c.id];
                          }
                        }}
                        onMouseEnter={() => {
                          if (canHoverFeeDetail()) {
                            openFeeDetail(c.id);
                          }
                        }}
                        onMouseLeave={() => {
                          if (canHoverFeeDetail()) {
                            closeFeeDetail();
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={s.feeQuestion}
                          onClick={() => toggleFeeDetail(c.id)}
                          aria-label="Ver detalle de mora"
                          aria-expanded={activeFeeDetailId === c.id ? "true" : "false"}
                        >
                          <i className="bi bi-question-circle-fill" aria-hidden="true" />
                        </button>
                        {activeFeeDetailId === c.id && feeDetailById[c.id] ? (
                          <div className={s.feeDetail} role="tooltip">
                            <div className={s.feeDetailHeader}>
                              <span className={s.feeDetailEyebrow}>Detalle de mora</span>
                            </div>
                            <p>
                              Cuota original: <strong>${feeDetailById[c.id].original_price}</strong>
                            </p>
                            <p>
                              Atraso por mora: <strong>${feeDetailById[c.id].interest}</strong>
                            </p>
                            <p>
                              Total: <strong>${feeDetailById[c.id].original_price + feeDetailById[c.id].interest}</strong>
                            </p>
                            <p className={s.feeDetailNote}>Monto redondeado a valores sin decimales</p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}

              {cuotasConEstilo
                .filter((c) => Number(c.is_extra || 0) > 0)
                .map((c) => {
                  const isExtraCloth = Number(c.is_extra) === 2;
                  return (
                    <div
                      key={c.id}
                      className={`${s.feeRow} ${isExtraCloth ? s.feeRowPrendaExtra : s.feeRowExtra}`}
                    >
                      <span className={s.feeNum}>+</span>
                      <div className={s.feeBody}>
                        <span className={s.feeName}>{isExtraCloth ? "Prenda extra" : "Cuota extra"}</span>
                        <span className={s.feeDate}>{c.formattedDate}</span>
                      </div>
                      <span className={s.feeAmount}>
                        ${parseFloat(c.montoConMora).toLocaleString("es-AR")}
                      </span>
                      <span className={s.feeDot} />
                    </div>
                  );
                })}
            </div>

            {paymentInformation?.items && (
              <details className={s.itemsDetail}>
                <summary className={s.itemsDetailSummary}>
                  Ver detalle de prendas y financiamiento
                </summary>
                <ul className={s.itemsList}>
                  {paymentInformation.items.map((item, i) => (
                    <li key={i} className={s.itemsListItem}>
                      <span
                        className={s.itemName}
                        style={{ color: item.isExtraOrder ? "#D449B8" : "#0072CE" }}
                      >
                        {item.name} {item.alias}
                        <span className={s.itemQty}> × {item.quantity}</span>
                      </span>
                      <span className={s.itemPrice}>
                        ${parseFloat(item.price).toLocaleString("es-AR")} c/u
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={s.financRow}>
                  <span>Financiamiento:</span>
                  <strong>
                    {paymentInformation.price_surcharge > 0
                      ? `+$${paymentInformation.price_surcharge}`
                      : paymentInformation.price_discount > 0
                        ? `-$${paymentInformation.price_discount} OFF`
                        : "$0"}
                  </strong>
                  {paymentInformation.extraQuantity > 0 && (
                    <span>
                      │ Adicional por minoría: <strong>${paymentInformation.extraQuantity}</strong>
                    </span>
                  )}
                  {paymentInformation.extraDesign > 0 && (
                    <span>
                      │ Cambio de diseño: <strong>${paymentInformation.extraDesign}</strong>
                    </span>
                  )}
                </div>
              </details>
            )}
          </section>
        )}

        <div className={s.pageSpacer} />
      </div>
    </>
  );
}

export default Payments;
